import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All requests send cookies automatically
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  // token kept in memory only (not localStorage) — httpOnly cookie handles persistence
  const [token, setToken] = useState(null);

  // ── Impersonation state ──────────────────────────────────────────────────
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationRole, setImpersonationRole] = useState(null);
  // Ref avoids stale-closure issues when switching roles rapidly
  const preImpersonationRef = useRef({ token: null, user: null });

  // On mount: re-hydrate session via httpOnly cookie.
  // Skip the network call on public routes with no auth UI (/prototype tablet demo,
  // and the marketing homepage at /) — avoids 401 console noise.
  useEffect(() => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPublicNoAuth = path.startsWith('/prototype') || path === '/';
    if (isPublicNoAuth) { setUser(null); return; }
    axios.get(`${API}/auth/me`)
      .then(res => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: t, user: u } = res.data;
    // Store token in memory for WebSocket query param usage
    setToken(t);
    setUser(u);
    return u;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { token: t, user: u } = res.data;
    setToken(t);
    setUser(u);
    return u;
  };

  const logout = async () => {
    // End impersonation audit log if active
    if (isImpersonating) {
      try {
        await axios({ url: `${API}/auth/end-impersonation`, method: 'POST',
          headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {}
      preImpersonationRef.current = { token: null, user: null };
      setIsImpersonating(false);
      setImpersonationRole(null);
      try { sessionStorage.removeItem('aos_imp_role'); } catch (_e) { /* ignore */ }
    }
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error('Logout endpoint error (non-critical):', e);
    }
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch (e) {
      setUser(null);
      return null;
    }
  };

  const hydrateIfNeeded = async () => {
    // When user is null (e.g. we shortcut hydration on the public /prototype route)
    // but we now need an authenticated view, try to recover from the httpOnly cookie
    // before letting ProtectedRoute bounce to /login.
    if (user === null) {
      return refreshUser();
    }
    return user;
  };

  const authFetch = (url, options = {}) => {
    // Cookie is sent automatically by axios (withCredentials: true)
    // Also send Authorization header when token is in memory (belt + suspenders)
    const headers = { ...options.headers };
    if (token) headers.Authorization = `Bearer ${token}`;
    return axios({ url: `${API}${url}`, ...options, headers });
  };

  // ── switchRole: start or switch impersonation using founder's original token ──
  const switchRole = async (targetRole) => {
    const founderToken = isImpersonating ? preImpersonationRef.current.token : token;
    const founderUser  = isImpersonating ? preImpersonationRef.current.user  : user;

    // Close current audit log if already impersonating
    if (isImpersonating) {
      try {
        await axios({
          url: `${API}/auth/end-impersonation`,
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
      } catch (e) {}
    }

    // Request new impersonation token using founder's original token
    // (falls back to the httpOnly cookie when no in-memory token, e.g. after reload)
    const impHeaders = { 'Content-Type': 'application/json' };
    if (founderToken) impHeaders.Authorization = `Bearer ${founderToken}`;
    const res = await axios({
      url: `${API}/auth/impersonate`,
      method: 'POST',
      headers: impHeaders,
      data: { role: targetRole },
      withCredentials: true,
    });

    const { token: impToken, user: impUser } = res.data;
    preImpersonationRef.current = { token: founderToken, user: founderUser };
    setToken(impToken);
    setUser(impUser);
    setIsImpersonating(true);
    setImpersonationRole(targetRole);
    try { sessionStorage.setItem('aos_imp_role', targetRole); } catch (_e) { /* ignore */ }
    return impUser;
  };

  // ── endImpersonation: restore founder session ──────────────────────────────
  const endImpersonation = async () => {
    if (!isImpersonating) return;
    try {
      await axios({
        url: `${API}/auth/end-impersonation`,
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
    } catch (e) {}
    const { token: origToken, user: origUser } = preImpersonationRef.current;
    preImpersonationRef.current = { token: null, user: null };
    setToken(origToken);
    // Founder user may be unknown after a reload-restored impersonation — refetch from cookie.
    if (origUser) setUser(origUser);
    else refreshUser();
    setIsImpersonating(false);
    setImpersonationRole(null);
    try { sessionStorage.removeItem('aos_imp_role'); } catch (_e) { /* ignore */ }
  };

  const contextValue = useMemo(
    () => ({
      user, token, login, register, logout, refreshUser, hydrateIfNeeded, authFetch, API,
      isImpersonating, impersonationRole, switchRole, endImpersonation,
    }),
    [user, token, isImpersonating, impersonationRole] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
